import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Popover } from 'antd';
import localStorageUtil from '$/common/utils/localStorageUtil';
import IconFont from '$/components/IconFont';
import { useLanguageStore, LanguageCodeType, getLanguageSelectConfig } from '$/store/languageStore';
import classNames from 'classnames';
import styles from './index.module.scss';

interface LanguageSwitchProps {
  /** 受控语言由宿主提供，跳过组件内部的工作台语言偏好读取。 */
  value?: LanguageCodeType;
  /** 提供回调时由宿主保存偏好，组件不再写入通用 lang 存储。 */
  onChange?: (value: LanguageCodeType | '') => void;
  /** 宿主可收敛可选语言；不传时沿用完整语言列表。 */
  options?: ReturnType<typeof getLanguageSelectConfig>;
}

/** 官网可受控提供语言与保存逻辑，其他页面保持现有全站语言切换行为。 */
export default function LanguageSwitch({ value, onChange, options: customOptions }: LanguageSwitchProps = {}) {
  const languageStore = useLanguageStore((state) => state.languageStore);
  const [languageCode, setLanguageCode] = useState<LanguageCodeType | ''>('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (value !== undefined) return;
    setLanguageCode(
      (localStorageUtil.getItem('lang') as LanguageCodeType) || '',
    );
  }, [value]);

  const options = customOptions || getLanguageSelectConfig();
  const currentLanguageCode = value ?? languageCode;
  const currentLabel = options.find((o) => o.value === currentLanguageCode)?.label
    || languageStore.smartcatTranslationText(
      (v) => v.RoboNeo_Web_toast_setting_follow_system,
    );

  /** 受控模式交给官网保存独立偏好，避免污染工作台的语言设置。 */
  const handleChange = (nextLanguage: LanguageCodeType | '') => {
    if (onChange) {
      setOpen(false);
      onChange(nextLanguage);
      return;
    }
    setLanguageCode(nextLanguage);
    localStorageUtil.setItem('lang', nextLanguage);
    Cookies.set('lang', nextLanguage);
    window.mtstat?.track('system_settings_click', {
      language: nextLanguage === '' ? 'system' : nextLanguage,
    });
    setOpen(false);
    setTimeout(() => {
      window.location.reload();
    });
  };

  const content = (
    <div className={styles.dropdownContent}>
      {options.map((option) => (
        <div
          key={option.value}
          className={classNames(styles.optionItem, {
            [styles.active]: option.value === currentLanguageCode,
          })}
          onClick={() => handleChange(option.value)}
        >
          <span className={styles.checkIcon}>
            {option.value === currentLanguageCode && (
              <IconFont name="checkBoxMark" fontSize={14} />
            )}
          </span>
          <span>{option.label}</span>
        </div>
      ))}
    </div>
  );

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      placement="bottomRight"
      trigger={['click']}
      overlayClassName="resetPopoverAndArrow"
      content={content}
    >
      <div className={styles.languageSwitch}>
        <IconFont name="earthBold" fontSize={16} />
        <span className={styles.label}>{currentLabel}</span>
      </div>
    </Popover>
  );
}
